import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  ComponentProps,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type HTMLAttributes,
  type RefObject,
  forwardRef,
} from "react";
import { twMerge } from "tailwind-merge";
import styles from "./dialog.module.css";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={twMerge(
      styles.dialogOverlay,
      "bg-grey-900/30 fixed inset-0 z-50",
      className,
    )}
    {...props}
  />
));

DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    triggerRef?: RefObject<HTMLElement | undefined | null>;
  }
>(({ className, children, triggerRef, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay>
      <DialogPrimitive.Content
        ref={ref}
        className={twMerge(
          styles.dialog,
          "fixed left-[50%] top-[50%] z-50 grid max-h-[calc(100%-32px)] w-[90vw] max-w-sm translate-x-[-50%] translate-y-[-50%] gap-6 rounded-lg bg-white p-4 pt-5 shadow-lg outline-hidden sm:gap-8 sm:rounded-lg sm:p-6 sm:pt-6",
          className,
        )}
        onCloseAutoFocus={
          triggerRef == null
            ? undefined
            : () => {
                if (triggerRef.current) {
                  triggerRef.current.focus();
                }
              }
        }
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogOverlay>
  </DialogPortal>
));

DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogOverlaylessContent = ({
  className,
  children,
  ref,
  ...props
}: ComponentProps<typeof DialogPrimitive.Content>) => (
  <DialogPortal>
    <DialogPrimitive.Content
      ref={ref}
      className={twMerge(
        styles.dialog,
        "fixed left-[50%] top-[50%] z-50 grid max-h-[calc(100%-32px)] w-[90vw] max-w-sm origin-[0%_0%] translate-x-[-50%] translate-y-[-50%] gap-6 rounded-lg bg-white p-4 pt-5 shadow-lg outline-hidden sm:gap-8 sm:rounded-lg sm:p-6 sm:pt-6",
        className,
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
);

const DialogHeader = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={twMerge("flex flex-col space-y-2 text-left", className)}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={twMerge(
      "flex flex-col-reverse justify-end gap-3 sm:flex-row",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = forwardRef<
  ElementRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={twMerge("text-grey-900 text-lg font-medium", className)}
    {...props}
  />
));

DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={twMerge("text-grey-500 text-sm", className)}
    {...props}
  />
));

DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogOverlaylessContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
